import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import ChatArea from './components/ChatArea';
import SourcePanel from './components/SourcePanel';
import LoginPage from './pages/LoginPage';
import AdminLayout from './components/AdminLayout';
import WidenSettingsPage from './pages/WidenSettingsPage';
import FranConnectConfig from './components/FranConnectConfig';
import LitmosConfig from './components/LitmosConfig';
import ZendeskConfig from './components/ZendeskConfig';
import ConversationsPage from './components/ConversationsPage';
import { ChatMessage, ChatSession, Source } from './types';
import { v4 as uuidv4 } from 'uuid';
import apiService from './services/api';

// Base path for the app (matches homepage in package.json)
const BASENAME = '';

// Create a modern theme with Burn Boot Camp branding
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#00ACDD', // Burn Boot Camp Primary Blue
      light: '#33BDEA',
      dark: '#0078A0',
    },
    secondary: {
      main: '#FF6B35', // Burn Boot Camp Orange accent
      light: '#FF8A65',
      dark: '#E64A19',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1E293B',
      secondary: '#64748B',
    },
    grey: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#E2E8F0',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#64748B',
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A',
    },
  },
  typography: {
    fontFamily: '"Outfit", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      fontSize: '1.75rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 172, 221, 0.15)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
  },
});

const DRAWER_WIDTH = 280;
const SOURCE_PANEL_WIDTH = 320;

// Main Chat Component
const MainChat: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sourcePanelOpen, setSourcePanelOpen] = useState(!isMobile);
  const [currentSession, setCurrentSession] = useState<string>('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSources, setCurrentSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(false);

  // Initialize session on load
  useEffect(() => {
    const sessionId = uuidv4();
    setCurrentSession(sessionId);
    
    // Load sessions from localStorage
    const savedSessions = localStorage.getItem('bbc-chat-sessions');
    if (savedSessions) {
      const parsedSessions = JSON.parse(savedSessions);
      const sessionsWithDates = parsedSessions.map((session: any) => ({
        ...session,
        timestamp: new Date(session.timestamp)
      }));
      setSessions(sessionsWithDates);
    }
  }, []);

  // Auto-close panels on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setSourcePanelOpen(false);
    } else {
      setSidebarOpen(true);
      setSourcePanelOpen(true);
    }
  }, [isMobile]);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleToggleSourcePanel = () => {
    setSourcePanelOpen(!sourcePanelOpen);
  };

  const handleNewSession = () => {
    const sessionId = uuidv4();
    setCurrentSession(sessionId);
    setMessages([]);
    setCurrentSources([]);
  };

  const handleSelectSession = async (sessionId: string) => {
    setCurrentSession(sessionId);
    setLoading(true);
    
    try {
      const history = await apiService.getQueryHistory(sessionId);
      
      const loadedMessages: ChatMessage[] = [];
      const allSources: Source[] = [];
      
      const sortedHistory = [...history].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      for (const query of sortedHistory) {
        loadedMessages.push({
          id: uuidv4(),
          content: query.query,
          isUser: true,
          timestamp: new Date(query.timestamp),
        });
        
        const querySources: Source[] = [];
        if (query.sources_found && query.sources_found.length > 0) {
          for (const source of query.sources_found) {
            const formattedSource: Source = {
              source_type: source.source_type,
              source_id: source.source_id,
              title: source.title,
              content_snippet: source.content_snippet,
              confidence_score: source.confidence_score,
              document_url: source.document_url || source.metadata?.public_url || '',
              metadata: source.metadata,
            };
            querySources.push(formattedSource);
            allSources.push(formattedSource);
          }
        }

        loadedMessages.push({
          id: uuidv4(),
          content: query.response,
          isUser: false,
          timestamp: new Date(query.timestamp),
          sources: querySources,
        });
      }
      
      setMessages(loadedMessages);
      setCurrentSources(allSources);
    } catch (error) {
      console.error('Error loading session history:', error);
      setMessages([]);
      setCurrentSources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (message: string) => {
    const userMessage: ChatMessage = {
      id: uuidv4(),
      content: message,
      isUser: true,
      timestamp: new Date(),
    };
    
    // Create AI message immediately with streaming state (shows "Searching...")
    const aiMessageId = uuidv4();
    const aiMessage: ChatMessage = {
      id: aiMessageId,
      content: '',
      isUser: false,
      timestamp: new Date(),
      sources: [],
      isStreaming: true,
    };
    
    setMessages(prev => [...prev, userMessage, aiMessage]);
    setLoading(true); // Disable input while processing
    
    try {
      let fullContent = '';
      
      await apiService.askQuestionStream(
        {
          query: message,
          session_id: currentSession,
        },
        // onMetadata - received sources and intent analysis
        (metadata) => {
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, sources: metadata.sources, intentAnalysis: metadata.intent_analysis }
              : msg
          ));
          setCurrentSources(metadata.sources || []);
        },
        // onContent - received a chunk of text
        (text) => {
          fullContent += text;
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: fullContent }
              : msg
          ));
        },
        // onDone - streaming complete
        (response_time_ms) => {
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, isStreaming: false }
              : msg
          ));
          updateSessionInHistory(currentSession, message, fullContent);
          setLoading(false);
          console.log(`Query completed in ${response_time_ms}ms`);
        },
        // onError
        (error) => {
          console.error('Streaming error:', error);
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { 
                  ...msg, 
                  content: fullContent 
                    ? fullContent + "\n\n[Error: Response was interrupted]" 
                    : "I'm sorry, I encountered an error while processing your request. Please try again.",
                  isStreaming: false 
                }
              : msg
          ));
          setLoading(false);
        }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage: ChatMessage = {
        id: aiMessageId,
        content: "I'm sorry, I'm having trouble connecting to the backend service. Please try refreshing the page or contact support if the issue persists.",
        isUser: false,
        timestamp: new Date(),
        isStreaming: false,
      };
      setMessages(prev => [...prev, errorMessage]);
      setLoading(false);
    }
  };

  const updateSessionInHistory = (sessionId: string, userMessage: string, aiResponse: string) => {
    const sessionTitle = userMessage.length > 50 ? userMessage.substring(0, 50) + '...' : userMessage;
    
    setSessions(prevSessions => {
      const existingSessionIndex = prevSessions.findIndex(s => s.id === sessionId);
      
      if (existingSessionIndex >= 0) {
        const updatedSessions = [...prevSessions];
        updatedSessions[existingSessionIndex] = {
          ...updatedSessions[existingSessionIndex],
          lastMessage: aiResponse.length > 100 ? aiResponse.substring(0, 100) + '...' : aiResponse,
          timestamp: new Date(),
          messageCount: updatedSessions[existingSessionIndex].messageCount + 2,
        };
        
        localStorage.setItem('bbc-chat-sessions', JSON.stringify(updatedSessions));
        return updatedSessions;
      } else {
        const newSession: ChatSession = {
          id: sessionId,
          title: sessionTitle,
          lastMessage: aiResponse.length > 100 ? aiResponse.substring(0, 100) + '...' : aiResponse,
          timestamp: new Date(),
          messageCount: 2,
        };
        
        const updatedSessions = [newSession, ...prevSessions];
        
        localStorage.setItem('bbc-chat-sessions', JSON.stringify(updatedSessions));
        return updatedSessions;
      }
    });
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await apiService.deleteChatHistory(sessionId);
      
      setSessions(prevSessions => {
        const updatedSessions = prevSessions.filter(session => session.id !== sessionId);
        localStorage.setItem('bbc-chat-sessions', JSON.stringify(updatedSessions));
        return updatedSessions;
      });
      
      if (sessionId === currentSession) {
        handleNewSession();
      }
    } catch (error) {
      console.error('Error deleting chat history:', error);
      alert('Failed to delete chat history. Please try again.');
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Header
        onToggleSidebar={handleToggleSidebar}
        onToggleSourcePanel={handleToggleSourcePanel}
        sidebarOpen={sidebarOpen}
        sourcePanelOpen={sourcePanelOpen}
      />
      
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sessions={sessions}
        currentSession={currentSession}
        onNewSession={handleNewSession}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        width={DRAWER_WIDTH}
      />
      
      <ChatArea
        messages={messages}
        loading={loading}
        onSendMessage={handleSendMessage}
        sidebarOpen={sidebarOpen}
        sourcePanelOpen={sourcePanelOpen}
        drawerWidth={DRAWER_WIDTH}
        sourcePanelWidth={SOURCE_PANEL_WIDTH}
      />
      
      <SourcePanel
        open={sourcePanelOpen}
        onClose={() => setSourcePanelOpen(false)}
        sources={currentSources}
        width={SOURCE_PANEL_WIDTH}
      />
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Add Outfit font globally */}
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <Router basename={BASENAME}>
        <Routes>
          {/* Public Chat Route */}
          <Route path="/" element={<MainChat />} />
          
          {/* Admin Login */}
          <Route path="/admin/login" element={<LoginPage />} />
          
          {/* Protected Admin Routes */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/franconnect" replace />} />
            <Route path="franconnect" element={<FranConnectConfig />} />
            <Route path="widen" element={<WidenSettingsPage />} />
            <Route path="litmos" element={<LitmosConfig />} />
            <Route path="zendesk" element={<ZendeskConfig />} />
            <Route path="conversations" element={<ConversationsPage />} />
          </Route>
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
